import { useColorModeValue } from '@chakra-ui/react';

// ---------------------------------------------------------------------------
// Spacing tokens — Chakra scale units (1 unit = 4px)
// Use these instead of hardcoded pixel strings for consistent rhythm.
// ---------------------------------------------------------------------------
export const spacing = {
  /** Admin layout content area */
  page: { base: 5, md: 7 } as const,       // 20px / 28px
  /** Card internal padding (matches Card theme baseStyle) */
  card: 6,                                   // 24px
  /** Horizontal padding inside PageCard header / content rows */
  cardHeader: 6,                             // 24px
  /** Gap between PageCards / major sections on a page */
  sectionGap: 7,                             // 28px
  /** Gap between form fields in a vertical stack */
  fieldGap: 5,                               // 20px
  /** Small gap between inline elements (badges, icons, chips) */
  inlineGap: 2,                              // 8px
} as const;

// ---------------------------------------------------------------------------
// Typography presets — spread onto Text / Heading components.
//   e.g. <Text {...typography.pageTitle}>
// ---------------------------------------------------------------------------
/** Font family for headings — resolves via CSS custom property set by the aesthetic.
 *  Classic: "Urbanist", "Figtree", sans-serif
 *  Clubhouse: same (headings stay Urbanist; only h1/display gets Instrument Serif) */
export const headingFont = 'var(--heading-font, "Urbanist", "Figtree", sans-serif)';

/** Font family for page-level display titles — picks up Instrument Serif in Clubhouse */
export const displayFont = 'var(--display-font, "Urbanist", "Figtree", sans-serif)';

export const typography = {
  pageTitle:    { fontSize: '2xl', fontWeight: '700' as const, lineHeight: '120%', fontFamily: displayFont, letterSpacing: '-0.02em' },
  sectionTitle: { fontSize: 'lg',  fontWeight: '600' as const, lineHeight: '130%', fontFamily: headingFont, letterSpacing: '-0.01em' },
  body:         { fontSize: 'sm',  fontWeight: '400' as const },
  bodyBold:     { fontSize: 'sm',  fontWeight: '600' as const },
  caption:      { fontSize: 'xs',  fontWeight: '400' as const },
  tableHeader:  { fontSize: 'xs',  fontWeight: '500' as const, letterSpacing: '0.02em' },
  label:        { fontSize: 'sm',  fontWeight: '600' as const },
  formLabel:    { fontSize: 'xs',  fontWeight: '600' as const, textTransform: 'uppercase' as const, letterSpacing: '0.06em' },
  formSectionLabel: { fontSize: 'xs', fontWeight: '700' as const, textTransform: 'uppercase' as const, letterSpacing: '0.08em', fontFamily: headingFont },
} as const;

/**
 * Clubhouse-enhanced typography overrides.
 * Use `useClubhouseTypography()` to get aesthetic-aware page title props.
 * Falls back to standard typography when Classic aesthetic is active.
 */
export function useClubhouseTypography() {
  // Read CSS custom property set by the theme — avoids importing AppSettingsContext
  // The var(--display-font) is set by buildGlobalStyles based on the aesthetic
  return {
    /** Page title — uses Instrument Serif display font when Clubhouse */
    pageTitle: {
      ...typography.pageTitle,
      fontFamily: 'var(--display-font, "Urbanist", "Figtree", sans-serif)',
    },
    /** Section title with heading font */
    sectionTitle: {
      ...typography.sectionTitle,
      fontFamily: 'var(--heading-font, "Urbanist", "Figtree", sans-serif)',
    },
    /** Data display — mono font for numbers when Clubhouse */
    dataValue: {
      fontFamily: 'var(--mono-font, inherit)',
      fontFeatureSettings: '"tnum" 1',
    },
  } as const;
}

// ---------------------------------------------------------------------------
// Semantic color pairs — [lightMode, darkMode].
// Use via the useSemanticColor() hook below.
// ---------------------------------------------------------------------------
const semanticColors = {
  textPrimary:      ['secondaryGray.900', 'white'],
  textSecondary:    ['secondaryGray.600', 'gray.400'],
  textMuted:        ['gray.500', 'gray.500'],
  border:           ['secondaryGray.100', 'whiteAlpha.100'],
  cardBg:           ['white', 'navy.800'],
  pageBg:           ['secondaryGray.300', 'navy.900'],
  tableRowHoverBg:  ['gray.50', 'whiteAlpha.50'],
  iconMuted:        ['gray.400', 'gray.500'],
} as const;

export type SemanticColorKey = keyof typeof semanticColors;

export function useSemanticColor(key: SemanticColorKey): string {
  const [light, dark] = semanticColors[key];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useColorModeValue(light, dark);
}

// ---------------------------------------------------------------------------
// Border radius scale
// ---------------------------------------------------------------------------
export const radii = {
  sm: '6px',
  md: '10px',
  lg: '14px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
} as const;

// ---------------------------------------------------------------------------
// Opacity tokens for interactive states
// ---------------------------------------------------------------------------
export const opacity = {
  disabled: 0.4,
  hover: 0.85,
  pressed: 0.7,
} as const;
